import {
  FileUploadResponse,
  InvalidLink,
  Link,
  LinkingGroupItem,
  ProgressHandler,
  LinkAllocation
} from '../../store/Link';
import { apiService } from '../APIService';
import { ApiAdvancedFilter, PaginatedResponse } from '../../store/GeneralStore';
import { buildAdvancedQueryURL } from '../../utils/helpers';
import { RpmAccount } from '../../store/RpmAccount';
import { WorkflowEvent } from '../../store/WorkflowEvent';

const API_SERVER_URL = process.env.REACT_APP_API_SERVER_URL;

export interface BulkRPMLinkCreateResults {
  success: Link[];
  activated: Link[];
  duplicate: Link[];
  missingRpmAccount: Link[];
  missingAsset: Link[];
}

export interface SearchLinksRequest {
  rpmAccountIds: string[];
}

export interface LinkDataByAssetRequest {
  salesAgentId: string;
  assetField: string;
  supplierId?: string;
  commissionStatus?: string;
  actualFirstRun?: string;
}

export interface SearchLinkResponse {
  activeLinks: Link[];
  archivedLinks: Link[];
  unlinkedRpmAccounts: RpmAccount[];
  missingRpmAccounts: string[];
}

export const createLinkData = (linkData: Link) => {
  return apiService.callExternalApi<Link>({
    url: `${API_SERVER_URL}/links`,
    method: 'POST',
    headers: {
      'content-type': 'application/json'
    },
    data: linkData
  });
};

export const bulkCreateLinkData = (linkData: Link[], updateInactive = true) => {
  return apiService.callExternalApi<BulkRPMLinkCreateResults>({
    url: `${API_SERVER_URL}/links/bulk?updateInactive=${updateInactive}`,
    method: 'POST',
    headers: {
      'content-type': 'application/json'
    },
    data: linkData
  });
};

export const bulkValidateLinksByAsset = (linkData: LinkDataByAssetRequest) => {
  return apiService.callExternalApi<BulkRPMLinkCreateResults>({
    url: `${API_SERVER_URL}/links/bulk/validate-by-asset-field`,
    method: 'POST',
    headers: {
      'content-type': 'application/json'
    },
    data: linkData
  });
};

export const deleteLinkData = (id: string) => {
  return apiService.callExternalApi<Link>({
    url: `${API_SERVER_URL}/links/${id}`,
    method: 'DELETE',
    headers: {
      'content-type': 'application/json'
    }
  });
};

export const listInvalidLinks = () => {
  return apiService.callExternalApi<InvalidLink[]>({
    url: `${API_SERVER_URL}/links/invalid-links`,
    method: 'GET',
    headers: {
      'content-type': 'application/json'
    }
  });
};

export const listLinks = (filters?: ApiAdvancedFilter) => {
  const mergedFilters: ApiAdvancedFilter = {
    page: 1,
    pageSize: 100,
    ...filters,
    orders: filters?.orders ?? [{ sort: 'number', direction: 'desc' }]
  };

  return apiService.callExternalApi<PaginatedResponse<Link>>({
    url: buildAdvancedQueryURL(mergedFilters, 'links'),
    method: 'GET',
    headers: {
      'content-type': 'application/json'
    }
  });
};

export const listLinkingGroups = (accountId: string, supplierId?: string) => {
  let url = `${API_SERVER_URL}/links/groups/${accountId}`;
  if (supplierId) url += `?supplierId=${supplierId}`;
  return apiService.callExternalApi<LinkingGroupItem[][]>({
    url,
    method: 'GET',
    headers: {
      'content-type': 'application/json'
    }
  });
};

export const uploadXLFile = (file: File, progressHandler: ProgressHandler) => {
  const formData = new FormData();
  formData.append('file', file);
  return apiService.callExternalApi<FileUploadResponse>({
    url: `${API_SERVER_URL}/rpm-account-merge/upload`,
    method: 'post',
    headers: {
      'content-type': 'multipart/form-data'
    },
    data: formData,
    onUploadProgress: progressHandler
  });
};

export const patchLink = (link: Link) => {
  return apiService.callExternalApi<Link>({
    url: `${API_SERVER_URL}/links/${link.id}`,
    method: 'PUT',
    headers: {
      'content-type': 'application/json'
    },
    data: JSON.stringify(link)
  });
};

export const getLink = (id: string) => {
  return apiService.callExternalApi<Link>({
    url: `${API_SERVER_URL}/links/${id}`,
    method: 'GET',
    headers: {
      'content-type': 'application/json'
    }
  });
};

export const getLinkAllocationByLinkId = (id: string) => {
  return apiService.callExternalApi<LinkAllocation[]>({
    url: `${API_SERVER_URL}/links/${id}/allocations`,
    method: 'GET',
    headers: {
      'content-type': 'application/json'
    }
  });
};

export const getAllocationsByTypeId = (type: 'combined-assets' | 'rpm-accounts', id: string, runId?: number) => {
  return apiService.callExternalApi<LinkAllocation[]>({
    url: `${API_SERVER_URL}/${type}/${id}/link-allocation-history${runId ? `?runId=${runId}` : ''}`,
    method: 'GET',
    headers: {
      'content-type': 'application/json'
    }
  });
};

export const getAllocationBreakdown = (rpmAccountId: string, runId: number) => {
  return apiService.callExternalApi<LinkAllocation[]>({
    url: `${API_SERVER_URL}/rpm-accounts/${rpmAccountId}/allocation-breakdown/${runId}`,
    method: 'GET',
    headers: {
      'content-type': 'application/json'
    }
  });
};

export const searchLinks = (searchRequest: SearchLinksRequest) => {
  return apiService.callExternalApi<SearchLinkResponse>({
    url: `${API_SERVER_URL}/links/search`,
    method: 'POST',
    headers: {
      'content-type': 'application/json'
    },
    data: searchRequest
  });
};

export const bulkDeleteLinkData = (ids: string[]) => {
  return apiService.callExternalApi<Link[]>({
    url: `${API_SERVER_URL}/links/bulk-remove`,
    method: 'POST',
    headers: {
      'content-type': 'application/json'
    },
    data: { links: ids }
  });
};

export const addManualOverride = (
  id: string,
  overrideData: {
    overridePercent: number;
    allocationId?: string;
    repeatOverride?: boolean;
  }
) => {
  return apiService.callExternalApi<Link>({
    url: `${API_SERVER_URL}/links/${id}/manual-override`,
    method: 'PUT',
    headers: {
      'content-type': 'application/json'
    },
    data: overrideData
  });
};

export const removeManualOverride = (id: string, allocationId?: string) => {
  return apiService.callExternalApi<Link>({
    url: `${API_SERVER_URL}/links/${id}/manual-override`,
    method: 'DELETE',
    headers: {
      'content-type': 'application/json'
    },
    data: { allocationId }
  });
};

export const getCurrentWorkflowLinkAllocation = () => {
  return apiService.callExternalApi<WorkflowEvent>({
    url: `${API_SERVER_URL}/link-allocations/workflow/current`,
    method: 'GET',
    headers: { 'content-type': 'application/json' }
  });
};

export const startLinkAllocationsApi = () => {
  return apiService.callExternalApi<void>({
    url: `${API_SERVER_URL}/link-allocations/workflow/run`,
    method: 'POST',
    headers: { 'content-type': 'application/json' }
  });
};
